import { Button, Loader } from '@insify/design-system/components';
import {
  BandAid,
  Bank,
  BrokenGlass,
  Care,
  Collaborate,
  Document,
  DocumentFalse,
  Earth,
  Legal,
  Stock,
  Toxic,
} from '@insify/design-system/line-icons';
import {
  ExclusionsOverview,
  ShopOverlayBulletPointHighlight,
  ShopOverlayBulletPoints,
  ShopOverlayCard,
  ShopOverlayCardHeading,
  ShopOverlayCardListWithIcon,
  ShopOverlayContainer,
  ShopOverlayHeading,
} from '@insify/funnel-components';
import { Trans, useTranslation } from 'react-i18next';

import { useExclusionsContent } from '../../../../hooks/useExclusionContent';
import { useConditionsQuery } from '../../../../hooks/useQueries';

import * as styles from './LiabilityCardOverlay.module.css';

import type { I18NFunnelNamespace } from '../../../../../translations';
import type { Product, RiskCarrier } from '../../../../models/queries';

type CopyWithIcon = {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  copy: I18NFunnelNamespace;
};

const rcProListWakam: CopyWithIcon[] = [
  {
    Icon: BandAid,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_wakam_1',
  },
  {
    Icon: BrokenGlass,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_wakam_2',
  },
  {
    Icon: Legal,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_wakam_3',
  },
  {
    Icon: Stock,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_wakam_4',
  },
  {
    Icon: Collaborate,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_wakam_5',
  },
  {
    Icon: Legal,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_wakam_6',
  },
  {
    Icon: Earth,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_wakam_7',
  },
  {
    Icon: Care,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_wakam_8',
  },
  {
    Icon: Document,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_wakam_9',
  },
];

const rcProListMre: CopyWithIcon[] = [
  {
    Icon: BandAid,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_mre_axeria_1',
  },
  {
    Icon: BrokenGlass,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_mre_axeria_2',
  },
  {
    Icon: Legal,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_mre_axeria_3',
  },
  {
    Icon: Stock,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_mre_axeria_4',
  },
  {
    Icon: Collaborate,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_mre_axeria_5',
  },
  {
    Icon: Legal,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_mre_axeria_6',
  },
  {
    Icon: Earth,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_mre_axeria_7',
  },
  {
    Icon: Care,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_mre_axeria_8',
  },
  {
    Icon: Document,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_mre_axeria_9',
  },
];

const rcExploitationList: CopyWithIcon[] = [
  {
    Icon: BandAid,
    copy: 'coverage_liability_overlay_coverage_item_public_liability_1',
  },
  {
    Icon: BrokenGlass,
    copy: 'coverage_liability_overlay_coverage_item_public_liability_2',
  },
  {
    Icon: DocumentFalse,
    copy: 'coverage_liability_overlay_coverage_item_public_liability_3',
  },
  {
    Icon: Legal,
    copy: 'coverage_liability_overlay_coverage_item_public_liability_4',
  },
  {
    Icon: Toxic,
    copy: 'coverage_liability_overlay_coverage_item_public_liability_5',
  },
];

const rcProListHiscox: CopyWithIcon[] = [
  {
    Icon: BandAid,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_hiscox_1',
  },
  {
    Icon: BrokenGlass,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_hiscox_2',
  },

  {
    Icon: Bank,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_hiscox_12',
  },

  {
    Icon: Stock,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_hiscox_3',
  },
  {
    Icon: Collaborate,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_hiscox_4',
  },
  {
    Icon: Legal,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_hiscox_11',
  },
  {
    Icon: Care,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_hiscox_5',
  },
  {
    Icon: Document,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_hiscox_13',
  },
  {
    Icon: Earth,
    copy: 'coverage_liability_overlay_coverage_item_profesional_liability_hiscox_10',
  },
];

const rcExploitationHiscoxList: CopyWithIcon[] = [
  {
    Icon: BandAid,
    copy: 'coverage_liability_overlay_coverage_item_public_liability_hiscox_1',
  },
  {
    Icon: BrokenGlass,
    copy: 'coverage_liability_overlay_coverage_item_public_liability_hiscox_2',
  },
  {
    Icon: DocumentFalse,
    copy: 'coverage_liability_overlay_coverage_item_public_liability_hiscox_3',
  },
  {
    Icon: Legal,
    copy: 'coverage_liability_overlay_coverage_item_public_liability_hiscox_6',
  },
  {
    Icon: Toxic,
    copy: 'coverage_liability_overlay_coverage_item_public_liability_hiscox_5',
  },
];

const rcProLists: Record<RiskCarrier, CopyWithIcon[]> = {
  wakam: rcProListWakam,
  munichRe: rcProListMre,
  axeria: rcProListMre,
  hiscox: rcProListHiscox,
};

const rcExpLists: Record<RiskCarrier, CopyWithIcon[]> = {
  wakam: rcExploitationList,
  munichRe: rcExploitationList,
  axeria: rcExploitationList,
  hiscox: rcExploitationHiscoxList,
};

const Summary: React.FC<{ title: string; list: CopyWithIcon[]; extraContent?: React.ReactNode }> = ({
  list,
  title,
  extraContent,
}) => {
  const listItems = list.map(({ Icon, copy }) => ({
    key: copy,
    Icon,
    label: <Trans i18nKey={`funnel:${copy}`} components={{ bold: <strong /> }} />,
  }));

  return (
    <ShopOverlayCard>
      <ShopOverlayCardHeading>{title}</ShopOverlayCardHeading>
      <ShopOverlayCardListWithIcon items={listItems} />
      {extraContent}
    </ShopOverlayCard>
  );
};

const CheckMarks: React.FC<{ className?: string; list: I18NFunnelNamespace[] }> = ({ className, list }) => {
  const listItems = list.map((key) => ({
    key,
    element: (
      <Trans i18nKey={key} ns='funnel' components={{ bold: <ShopOverlayBulletPointHighlight /> }} parent='span' />
    ),
  }));

  return <ShopOverlayBulletPoints className={className} bulletPoints={listItems} />;
};

const ipidLinks = {
  wakam: 'https://media.insify.com/fr/2023/Insify_IPID_RC_FR_WAKAM-REBRANDED.pdf',
  munichRe: 'https://media.insify.com/fr/2023/Insify_IPID_RC_FR_MRE-REBRANDED.pdf',
  axeria:
    'https://media.insify.com/fr/2024/Document_information_produit_Assurance-Responsabilites-Civiles_RCa.2024a-A.pdf',
};

const ipidLinkByProduct: Record<Product, string> = {
  hiscoxRcProMisc: 'https://media.insify.com/fr/2023/Insify_IPID_HISCOX_RC_PRO_MISC.pdf',
  hiscoxRcProTech: 'https://media.insify.com/fr/2023/Insify_IPID_HISCOX_RC_PRO_TECH.pdf',
  hiscoxRcProMac: 'https://media.insify.com/fr/2023/Insify_IPID_HISCOX_RC_PRO_MAC.pdf',
  hiscoxRcProMan: 'https://media.insify.com/fr/2023/Insify_IPID_HISCOX_RC_PRO_MAN.pdf',
  hiscoxRcProTag: 'https://media.insify.com/fr/2023/Insify_IPID_HISCOX_RC_PRO_TAG.pdf',
  hiscoxRcProSecu: 'https://media.insify.com/fr/2023/Insify_IPID_HISCOX_RC_PRO_SECU.pdf',
  hiscoxRcProMiscBienEtre: 'https://media.insify.com/fr/2023/Insify_IPID_HISCOX_RC_PRO_MISC_BIEN_ETRE.pdf',
  hiscoxRcProMedia: 'https://media.insify.com/fr/2023/Insify_IPID_HISCOX_RC_PRO_MEDIA.pdf',
};

const ipidLinkForHiscoxRCExploitation = 'https://media.insify.com/fr/2023/Insify_IPID_HISCOX_RC_EXPLOITATION.pdf';

export const LiabilityCardOverlay: React.FC<{ riskCarrier?: RiskCarrier }> = ({ riskCarrier }) => {
  const { t } = useTranslation(['funnel']);
  const { exclusionsSegmentSpecific, translations, ...exclusionsContent } = useExclusionsContent();
  const { data: conditions } = useConditionsQuery();
  const liabilityExclusions = exclusionsSegmentSpecific?.filter(
    (exclusion) => exclusion.coverage === 'professionalLiability',
  );

  if (riskCarrier === undefined) {
    return (
      <div>
        <Loader loading />
      </div>
    );
  }

  return (
    <div>
      <ShopOverlayHeading>{t('funnel:coverage_liability_overlay_title')}</ShopOverlayHeading>
      <ShopOverlayContainer>
        {conditions?.professionalLiability?.dailyCancellable ?? true ? (
          <CheckMarks
            list={[
              'coverage_liability_overlay_usp3_no_commitment',
              'coverage_liability_overlay_usp1_activity',
              'coverage_liability_overlay_usp2_quick',
            ]}
            className={styles.uspList}
          />
        ) : (
          <CheckMarks
            list={['coverage_liability_overlay_usp1_activity', 'coverage_liability_overlay_usp2_quick']}
            className={styles.uspList}
          />
        )}
        <Summary
          title={t(`funnel:coverage_liability_overlay_coverage_title_profesional_liability`)}
          list={rcProLists[riskCarrier]}
        />
        {riskCarrier === 'hiscox' && (
          <Button
            trackingId='see-terms'
            className={styles.overlayButton}
            type='button'
            kind='secondary'
            href={ipidLinkByProduct[conditions?.products?.[0] as Product]}
            target='_blank'
          >
            {t('funnel:coverage_liability_overlay_button_copy')}
          </Button>
        )}
        <Summary
          title={t(`funnel:coverage_liability_overlay_coverage_title_public_liability`)}
          list={rcExpLists[riskCarrier]}
          extraContent={
            <CheckMarks
              className={styles.recommendation}
              list={['coverage_liability_overlay_recommendation_checkmark']}
            />
          }
        />

        {riskCarrier === 'hiscox' ? (
          <Button
            trackingId='see-terms'
            className={styles.overlayButton}
            type='button'
            kind='secondary'
            href={ipidLinkForHiscoxRCExploitation}
            target='_blank'
          >
            {t('funnel:coverage_liability_overlay_button_copy')}
          </Button>
        ) : (
          <Button
            trackingId='see-terms'
            className={styles.overlayButton}
            type='button'
            kind='secondary'
            href={ipidLinks[riskCarrier]}
            target='_blank'
          >
            {t('funnel:coverage_liability_overlay_button_copy')}
          </Button>
        )}
        <div className={styles.exclusions}>
          <ShopOverlayCardHeading>{t('funnel:coverage_liability_overlay_info_title')}</ShopOverlayCardHeading>
          <ExclusionsOverview
            translations={{
              ...translations,
              accordionProfessionalLiabilityHeader: t('funnel:coverage_liability_exclusion_accordion_header'),
              accordionProfessionalLiabilityDetailsHeader: t(
                'funnel:coverage_liability_exclusion_accordion_details_header',
              ),
            }}
            exclusionsSegmentSpecific={liabilityExclusions}
            {...exclusionsContent}
          />
        </div>
      </ShopOverlayContainer>
    </div>
  );
};
